// Reports: informational alerts from "report services" (label
// erecords/kind=report). See erecords/reports.go for the server side.
import { gql, useMutation, useQuery } from 'urql'

// GoAlert's urql client suspends by default; render our own loading state.
const noSuspense = { suspense: false }

export const REPORT_LABEL_KEY = 'erecords/kind'
export const REPORT_LABEL_VALUE = 'report'

// isFailure: summaries that stay real alerts. Keep in sync with
// reportFailure in erecords/reports.go.
export function isFailure(summary: string): boolean {
  return /\b(fail|failed|failing|failure|error|errors|down|broken|critical)\b/i.test(
    summary,
  )
}

export type Outcome = 'passed' | 'failed' | 'info'
export function outcomeOf(summary: string): Outcome {
  if (isFailure(summary)) return 'failed'
  if (/\b(pass|passed|ok|success|succeeded|green)\b/i.test(summary))
    return 'passed'
  return 'info'
}

const servicesQuery = gql`
  query ErecordsReportServices {
    services(input: { first: 100 }) {
      nodes {
        id
        name
        labels {
          key
          value
        }
      }
    }
  }
`

const setLabel = gql`
  mutation ErecordsSetReportLabel($input: SetLabelInput!) {
    setLabel(input: $input)
  }
`

export type ServiceKind = { id: string; name: string; report: boolean }

// useServiceKinds lists services and whether each is a report service.
export function useServiceKinds(): {
  services: ServiceKind[]
  loaded: boolean
  setReport: (id: string, on: boolean) => Promise<unknown>
} {
  const [q] = useQuery({
    query: servicesQuery,
    context: noSuspense,
    requestPolicy: 'cache-and-network',
  })
  const [, commit] = useMutation(setLabel)
  const services: ServiceKind[] = (q.data?.services?.nodes ?? []).map(
    (s: {
      id: string
      name: string
      labels: { key: string; value: string }[]
    }) => ({
      id: s.id,
      name: s.name,
      report: s.labels.some(
        (l) => l.key === REPORT_LABEL_KEY && l.value === REPORT_LABEL_VALUE,
      ),
    }),
  )
  return {
    services,
    loaded: Boolean(q.data),
    setReport: (id, on) =>
      commit(
        {
          input: {
            target: { type: 'service', id },
            key: REPORT_LABEL_KEY,
            value: on ? REPORT_LABEL_VALUE : '',
          },
        },
        { additionalTypenames: ['Service', 'Label'] },
      ),
  }
}
